import axios from 'axios';

// Défini dans .env.development (local) et .env.production (prod).
const API_URL = process.env.REACT_APP_API_URL || 'http://192.168.1.41:8000/api';

const studioAPI = axios.create({
  baseURL: `${API_URL}/studio`,
  headers: {
    'Content-Type': 'application/json',
  },
});

studioAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

studioAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) throw new Error('No refresh token');

        const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        });

        const newToken = response.data.access;
        localStorage.setItem('access_token', newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return studioAPI(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Les `list` de ModelViewSet passent par la pagination DRF ({ results: [...] }),
 * les `@action` renvoient un tableau nu. On normalise toujours en tableau.
 */
export const extraireListe = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

/** Un envoi contenant un fichier doit partir en multipart. */
const configUpload = (data) =>
  (typeof FormData !== 'undefined' && data instanceof FormData)
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : {};

// ===========================
// CATALOGUE
// ===========================

export const categoriePrestationAPI = {
  getAll: () => studioAPI.get('/categories/'),
  create: (data) => studioAPI.post('/categories/', data),
  update: (id, data) => studioAPI.put(`/categories/${id}/`, data),
  // PATCH pour une bascule : un PUT partiel serait rejeté (`nom` obligatoire)
  patch: (id, data) => studioAPI.patch(`/categories/${id}/`, data),
  delete: (id) => studioAPI.delete(`/categories/${id}/`),
};

export const prestationAPI = {
  getAll: (params = {}) => studioAPI.get('/prestations/', { params }),
  getCatalogue: () => studioAPI.get('/prestations/catalogue/'),
  create: (data) => studioAPI.post('/prestations/', data),
  update: (id, data) => studioAPI.put(`/prestations/${id}/`, data),
  patch: (id, data) => studioAPI.patch(`/prestations/${id}/`, data),
  delete: (id) => studioAPI.delete(`/prestations/${id}/`),
};

export const designAPI = {
  getAll: (params = {}) => studioAPI.get('/designs/', { params }),
  create: (data) => studioAPI.post('/designs/', data),
  update: (id, data) => studioAPI.put(`/designs/${id}/`, data),
  patch: (id, data) => studioAPI.patch(`/designs/${id}/`, data),
  delete: (id) => studioAPI.delete(`/designs/${id}/`),
};

// ===========================
// ARTICLES VENDUS (boissons + accessoires de cheveux)
// ===========================

export const articleStudioAPI = {
  // params: { type: 'boisson' | 'accessoire' }
  getAll: (params = {}) => studioAPI.get('/articles/', { params }),
  getDisponibles: () => studioAPI.get('/articles/disponibles/'),
  getStockFaible: () => studioAPI.get('/articles/stock-faible/'),
  create: (data) => studioAPI.post('/articles/', data),
  update: (id, data) => studioAPI.put(`/articles/${id}/`, data),
  patch: (id, data) => studioAPI.patch(`/articles/${id}/`, data),
  delete: (id) => studioAPI.delete(`/articles/${id}/`),
};

export const approvisionnementAPI = {
  // params: { article, type, date_debut, date_fin }
  getAll: (params = {}) => studioAPI.get('/approvisionnements/', { params }),
  getResume: (params = {}) => studioAPI.get('/approvisionnements/resume/', { params }),
  // Historique en écriture seule : pas d'update ni de delete côté serveur
  create: (data) => studioAPI.post('/approvisionnements/', data),
};

// ===========================
// PRESTATAIRES
// ===========================

export const prestataireAPI = {
  getAll: (params = {}) => studioAPI.get('/prestataires/', { params }),
  getActifs: () => studioAPI.get('/prestataires/actifs/'),
  // Réservé au propriétaire/manager
  getCommissions: (params = {}) => studioAPI.get('/prestataires/commissions/', { params }),
  create: (data) => studioAPI.post('/prestataires/', data),
  update: (id, data) => studioAPI.put(`/prestataires/${id}/`, data),
  patch: (id, data) => studioAPI.patch(`/prestataires/${id}/`, data),
  delete: (id) => studioAPI.delete(`/prestataires/${id}/`),
};

// ===========================
// CLIENTS
// ===========================

export const clientStudioAPI = {
  getAll: (params = {}) => studioAPI.get('/clients/', { params }),
  recherche: (q) => studioAPI.get('/clients/recherche/', { params: { q } }),
  getFideles: () => studioAPI.get('/clients/fideles/'),
  getHistorique: (id) => studioAPI.get(`/clients/${id}/historique/`),
  create: (data) => studioAPI.post('/clients/', data),
  update: (id, data) => studioAPI.put(`/clients/${id}/`, data),
  patch: (id, data) => studioAPI.patch(`/clients/${id}/`, data),
  delete: (id) => studioAPI.delete(`/clients/${id}/`),
};

// ===========================
// TICKETS (supervision — la vente se fait au POS mobile)
// ===========================

export const ticketStudioAPI = {
  getAll: (params = {}) => studioAPI.get('/tickets/', { params }),
  getById: (id) => studioAPI.get(`/tickets/${id}/`),
  getOuverts: () => studioAPI.get('/tickets/ouverts/'),
  annuler: (id) => studioAPI.post(`/tickets/${id}/annuler/`),
};

// ===========================
// DÉPENSES
// ===========================

export const typeDepenseStudioAPI = {
  getAll: () => studioAPI.get('/types-depenses/'),
  create: (data) => studioAPI.post('/types-depenses/', data),
  update: (id, data) => studioAPI.put(`/types-depenses/${id}/`, data),
  patch: (id, data) => studioAPI.patch(`/types-depenses/${id}/`, data),
  delete: (id) => studioAPI.delete(`/types-depenses/${id}/`),
};

export const depenseStudioAPI = {
  getAll: (params = {}) => studioAPI.get('/depenses/', { params }),
  getResume: (params = {}) => studioAPI.get('/depenses/resume/', { params }),
  create: (data) => studioAPI.post('/depenses/', data, configUpload(data)),
  update: (id, data) => studioAPI.put(`/depenses/${id}/`, data, configUpload(data)),
  patch: (id, data) => studioAPI.patch(`/depenses/${id}/`, data, configUpload(data)),
  delete: (id) => studioAPI.delete(`/depenses/${id}/`),
};

// ===========================
// RAPPORTS
// ===========================

export const rapportStudioAPI = {
  getDashboard: () => studioAPI.get('/dashboard/'),
  // params: { date_debut, date_fin, client, q }
  getRapport: (params = {}) => studioAPI.get('/rapport/', { params }),
};

export default studioAPI;
